"use client";

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar } from '../components/ui/avatar';
import { ToggleGroup } from '../components/ui/toggle-group';
import { DropdownMenu } from '../components/ui/dropdown-menu';
import { IconWithBackground } from '../components/ui/icon-with-background';
import { getPeterConversations } from '../services/managerService';
import { 
    FeatherArrowLeft, 
    FeatherClock,
    FeatherCheckCircle,
    FeatherXCircle,
    FeatherAlertCircle,
    FeatherMessageSquare,
    FeatherChevronRight,
    FeatherSearch
} from '../components/ui/icons';
import ConversationDetail from '../components/PeterConversations/ConversationDetail';
import './PeterConversations.css';

const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
        case 'resolved':
            return 'success';
        case 'dismissed':
            return 'neutral';
        case 'in_progress':
        case 'waiting_for_time':
            return 'warning';
        case 'pending':
            return 'default';
        default:
            return 'default';
    }
};

const getTypeLabel = (type) => {
    switch (type) {
        case 'work_day_reminder':
            return 'Work Day Reminder';
        case 'task_blocker_analysis':
            return 'Task Blocker';
        case 'unified':
            return 'Unified';
        case 'work_day_closing':
            return 'Work Day Closing';
        default:
            return type;
    }
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
};

const formatRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
};

const PeterConversations = () => {
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        status: 'all',
        type: 'all',
        userId: null
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [users, setUsers] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetchConversations();
    }, [filters]);

    const fetchConversations = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const filterParams = {};
            if (filters.status !== 'all') {
                filterParams.status = filters.status;
            }
            if (filters.type !== 'all') {
                filterParams.type = filters.type;
            }
            if (filters.userId) {
                filterParams.userId = filters.userId;
            }

            const data = await getPeterConversations(filterParams);
            setConversations(data.conversations || []);
            
            // Extract unique users for dropdown
            const uniqueUsers = [...new Map(
                (data.conversations || []).map(conv => [conv.user_id, {
                    id: conv.user_id,
                    name: conv.user_name,
                    email: conv.user_email
                }])
            ).values()];
            setUsers(uniqueUsers);
        } catch (err) {
            setError('Failed to load conversations');
            console.error('Error fetching conversations:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = (conversation) => {
        setSelectedConversation(conversation);
        setShowDetail(true);
    };

    const handleCloseDetail = () => {
        setShowDetail(false);
        setSelectedConversation(null);
    };

    const filteredConversations = conversations.filter(conv => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            conv.user_name.toLowerCase().includes(query) ||
            conv.user_email.toLowerCase().includes(query) ||
            conv.bot_message?.toLowerCase().includes(query) ||
            conv.last_user_message?.content?.toLowerCase().includes(query)
        );
    });

    if (loading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <div className="loading">Loading conversations...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <div className="alert alert-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="peter-conversations-container">
            {/* Header section */}
            <div className="flex w-full flex-col items-start gap-6">
                <div className="flex w-full items-center gap-4">
                    <Button
                        variant="neutral-tertiary"
                        icon={<FeatherArrowLeft />}
                        onClick={() => navigate('/dashboard')}
                    >
                        Back
                    </Button>
                    <span className="grow text-heading-2 font-heading-2 text-default-font">
                        Peter Bot Conversations
                    </span>
                </div>

                {/* Filters */}
                <div className="flex w-full flex-col gap-4 md:flex-row md:items-center">
                    {/* Search */}
                    <div className="flex-1">
                        <div className="search-input-wrapper">
                            <FeatherSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search by user name, email, or message..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                        </div>
                    </div>

                    {/* Status Filter */}
                    <ToggleGroup 
                        value={filters.status} 
                        onValueChange={(value) => setFilters({ ...filters, status: value })}
                    >
                        <ToggleGroup.Item value="all">All Status</ToggleGroup.Item>
                        <ToggleGroup.Item value="pending">Pending</ToggleGroup.Item>
                        <ToggleGroup.Item value="in_progress">In Progress</ToggleGroup.Item>
                        <ToggleGroup.Item value="waiting_for_time">Waiting</ToggleGroup.Item>
                        <ToggleGroup.Item value="resolved">Resolved</ToggleGroup.Item>
                        <ToggleGroup.Item value="dismissed">Dismissed</ToggleGroup.Item>
                    </ToggleGroup>

                    {/* Type Filter */}
                    <ToggleGroup 
                        value={filters.type} 
                        onValueChange={(value) => setFilters({ ...filters, type: value })}
                    >
                        <ToggleGroup.Item value="all">All Types</ToggleGroup.Item>
                        <ToggleGroup.Item value="work_day_reminder">Reminders</ToggleGroup.Item>
                        <ToggleGroup.Item value="task_blocker_analysis">Blockers</ToggleGroup.Item>
                        <ToggleGroup.Item value="unified">Unified</ToggleGroup.Item>
                    </ToggleGroup>

                    {/* User Filter */}
                    {users.length > 0 && (
                        <DropdownMenu>
                            <DropdownMenu.Trigger asChild>
                                <Button variant="neutral-secondary">
                                    {filters.userId 
                                        ? users.find(u => u.id === filters.userId)?.name || 'User'
                                        : 'All Users'
                                    }
                                    <FeatherChevronRight className="w-4 h-4 ml-2" style={{ transform: 'rotate(90deg)' }} />
                                </Button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content>
                                <DropdownMenu.Item onClick={() => setFilters({ ...filters, userId: null })}>
                                    All Users
                                </DropdownMenu.Item>
                                {users.map(user => (
                                    <DropdownMenu.Item 
                                        key={user.id}
                                        onClick={() => setFilters({ ...filters, userId: user.id })}
                                    >
                                        {user.name}
                                    </DropdownMenu.Item>
                                ))}
                            </DropdownMenu.Content>
                        </DropdownMenu>
                    )}
                </div>
            </div>

            {/* Conversations List */}
            <div className="conversations-list">
                {filteredConversations.length === 0 ? (
                    <div className="empty-state">
                        <FeatherMessageSquare className="empty-icon" />
                        <p>No conversations found</p>
                    </div>
                ) : (
                    filteredConversations.map(conv => (
                        <div key={conv.id} className="conversation-card">
                            {/* Header */}
                            <div className="conversation-header">
                                <div className="flex items-center gap-3">
                                    <Avatar size="medium">
                                        {conv.user_name.charAt(0)}
                                    </Avatar>
                                    <div className="flex flex-col">
                                        <span className="conversation-user-name">{conv.user_name}</span>
                                        <span className="conversation-user-email">{conv.user_email}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge variant={getStatusVariant(conv.status)}>
                                        {conv.status}
                                    </Badge>
                                    <Badge variant="neutral">
                                        {getTypeLabel(conv.conversation_type)}
                                    </Badge>
                                    {conv.goal_achieved && (
                                        <Badge variant="success" icon={<FeatherCheckCircle />}>
                                            Goal Achieved
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            {/* Last User Message */}
                            {conv.last_user_message && (
                                <div className="last-message-section">
                                    <div className="section-label">Last User Message:</div>
                                    <div className="message-content">
                                        {conv.last_user_message.content}
                                    </div>
                                    <div className="message-time">
                                        {formatRelativeTime(conv.last_user_message.timestamp)}
                                    </div>
                                </div>
                            )}

                            {/* Next Planned Action */}
                            {conv.next_planned_action && (
                                <div className="next-action-section">
                                    <div className="section-label">Next Planned Action:</div>
                                    <div className="action-content">
                                        <div className="action-title">{conv.next_planned_action.action}</div>
                                        {conv.next_planned_action.action_plan && (
                                            <div className="action-plan">
                                                <strong>Action Plan:</strong> {conv.next_planned_action.action_plan}
                                            </div>
                                        )}
                                        {!conv.next_planned_action.action_plan && conv.next_planned_action.description && (
                                            <div className="action-description">{conv.next_planned_action.description}</div>
                                        )}
                                        {conv.next_planned_action.has_followup && conv.next_planned_action.follow_up_time && (
                                            <div className="action-schedule highlight">
                                                <FeatherClock className="w-4 h-4" />
                                                <div className="followup-info">
                                                    <span className="followup-label">Next Follow-up Time:</span>
                                                    <span className="followup-time">{formatDate(conv.next_planned_action.follow_up_time)}</span>
                                                    {conv.next_planned_action.time_until_followup && (
                                                        <span className="time-until">({conv.next_planned_action.time_until_followup})</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {conv.next_planned_action.scheduled_at && !conv.next_planned_action.has_followup && (
                                            <div className="action-schedule">
                                                <FeatherClock className="w-4 h-4" />
                                                <span>Scheduled: {formatDate(conv.next_planned_action.scheduled_at)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Bot Message Preview */}
                            <div className="bot-message-preview">
                                <div className="section-label">Bot Message:</div>
                                <div className="message-content">
                                    {conv.bot_message || 'No message'}
                                </div>
                            </div>

                            {/* Metadata */}
                            <div className="conversation-metadata">
                                <div className="metadata-item">
                                    <span className="metadata-label">Created:</span>
                                    <span className="metadata-value">{formatRelativeTime(conv.created_at)}</span>
                                </div>
                                <div className="metadata-item">
                                    <span className="metadata-label">Updated:</span>
                                    <span className="metadata-value">{formatRelativeTime(conv.updated_at)}</span>
                                </div>
                                {conv.scheduled_follow_up_at && (
                                    <div className="metadata-item highlight">
                                        <FeatherClock className="w-4 h-4" />
                                        <span className="metadata-label">Follow-up:</span>
                                        <span className="metadata-value">{formatDate(conv.scheduled_follow_up_at)}</span>
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="conversation-actions">
                                <Button
                                    variant="brand-secondary"
                                    onClick={() => handleViewDetail(conv)}
                                >
                                    View Details
                                </Button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Detail Modal */}
            {showDetail && selectedConversation && (
                <ConversationDetail
                    conversation={selectedConversation}
                    onClose={handleCloseDetail}
                />
            )}
        </div>
    );
};

export default PeterConversations;

